import { 
  listenToUser, 
  listenToUserChatSessions, 
  listenToGlobalConfig 
} from './firebaseService';
import { User, ChatSession, GlobalConfig } from '../types';

/**
 * Real-Time Sync Service
 * 
 * This service provides real-time synchronization features using Firestore listeners.
 * Changes in the database are instantly reflected in the UI.
 */

type UnsubscribeFunction = () => void;

// Store active listeners to prevent duplicates
const activeListeners = new Map<string, UnsubscribeFunction>();

/**
 * Start listening to user changes in real-time
 * @param userId - The user ID to listen to
 * @param callback - Called when user data changes
 * @returns Unsubscribe function
 */
export const startUserSync = (
  userId: string, 
  callback: (user: User | null) => void
): UnsubscribeFunction => {
  // Guard against undefined userId
  if (!userId) {
    return () => {}; // Return noop unsubscribe
  }
  
  const key = `user-${userId}`;
  
  // If already listening, skip duplicate
  if (activeListeners.has(key)) {
    return activeListeners.get(key)!;
  }
  
  const unsubscribe = listenToUser(userId, callback);
  activeListeners.set(key, unsubscribe);
  
  return () => {
    unsubscribe();
    activeListeners.delete(key);
  };
};

/**
 * Start listening to chat sessions in real-time
 * @param userId - The user ID (or undefined for guest sessions)
 * @param callback - Called when sessions change
 * @returns Unsubscribe function
 */
export const startSessionsSync = (
  userId: string | undefined,
  callback: (sessions: ChatSession[]) => void
): UnsubscribeFunction => {
  const key = `sessions-${userId || 'guest'}`;
  
  // If already listening, skip duplicate
  if (activeListeners.has(key)) {
    return activeListeners.get(key)!;
  }
  
  const unsubscribe = listenToUserChatSessions(userId, callback);
  activeListeners.set(key, unsubscribe);
  
  return () => {
    unsubscribe();
    activeListeners.delete(key);
  };
};

/**
 * Start listening to global config in real-time
 * @param callback - Called when config changes
 * @returns Unsubscribe function
 */
export const startConfigSync = (
  callback: (config: GlobalConfig | null) => void
): UnsubscribeFunction => {
  const key = 'config-global';
  
  // If already listening, skip duplicate
  if (activeListeners.has(key)) {
    return activeListeners.get(key)!;
  }
  
  const unsubscribe = listenToGlobalConfig(callback);
  activeListeners.set(key, unsubscribe);
  
  return () => {
    unsubscribe();
    activeListeners.delete(key);
  };
};

/**
 * Stop all active real-time listeners
 */
export const stopAllSync = (): void => {
  // Silently stop all listeners to reduce console spam
  activeListeners.forEach((unsubscribe) => {
    unsubscribe();
  });
  
  activeListeners.clear();
};

/**
 * Get count of active listeners
 */
export const getActiveListenersCount = (): number => {
  return activeListeners.size;
};

/**
 * Check if a specific listener is active
 */
export const isListenerActive = (type: 'user' | 'sessions' | 'config', id?: string): boolean => {
  const key = type === 'user' && id ? `user-${id}` :
              type === 'sessions' ? `sessions-${id || 'guest'}` :
              type === 'config' ? 'config-global' : '';
  
  return activeListeners.has(key);
};

export default {
  startUserSync,
  startSessionsSync,
  startConfigSync,
  stopAllSync,
  getActiveListenersCount,
  isListenerActive
};
